import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Shield, Edit3, Users } from "lucide-react";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await fetch(`${API}/api/team`);
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
      }
    } catch (error) {
      console.error("Error fetching team:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Notre Équipe"
        description="Découvrez les membres de notre équipe éditoriale."
      />
      <div className="min-h-screen pt-20 pb-16" data-testid="team-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              Notre Équipe
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Les personnes derrière le contenu de ce site
            </p>
          </motion.div>

          <div className="grid gap-4 md:gap-6">
            {members.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 p-4 md:p-6 rounded-xl bg-card border border-border"
                data-testid={`team-member-${member.id}`}
              >
                <Avatar className="h-14 w-14 md:h-16 md:w-16">
                  {member.profile_image ? (
                    <AvatarImage src={`${API}${member.profile_image}`} alt={member.full_name} />
                  ) : null}
                  <AvatarFallback className={member.role === "admin" ? "bg-primary text-primary-foreground text-xl" : "bg-secondary text-xl"}>
                    {member.full_name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      {member.full_name}
                    </h2>
                    {member.role === "admin" ? (
                      <Badge className="gap-1 bg-primary">
                        <Shield className="h-3 w-3" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <Edit3 className="h-3 w-3" />
                        Éditeur
                      </Badge>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {members.length === 0 && (
            <p className="text-center text-muted-foreground py-12">
              Aucun membre dans l'équipe pour le moment.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default TeamPage;
